import moment from "moment";
import { type ContractExtensionDto, type OptionDto } from "../../../autogen/bff-api";
import { isWholeExtensionNumber } from "../../../common/extension/extensionRules";
import { isDecided } from "./extensionStatus";

export type OptionKind = "locked" | "earlier" | "duration" | "byHand";

export const optionKind = (option: OptionDto): OptionKind => {
  if (isDecided(option)) return "locked";
  if (option.datesSetByHand) return "byHand";
  if (option.extension) return "duration";
  return "earlier";
};

export const isSavableExtension = (extension: ContractExtensionDto): boolean =>
  isWholeExtensionNumber(extension.duration) && isWholeExtensionNumber(extension.exerciseDeadlineMonths);

export const FIRST_SUPPORTED_DATE = "1900-01-01";
export const LAST_SUPPORTED_DATE = "9999-12-31";

export type HandSetDate = "startDate" | "endDate" | "deadline";

export interface HandSetDateProblems {
  endsBeforeStart: boolean;
  deadlineAfterStart: boolean;
  outsideSupportedRange: HandSetDate[];
}

const isOutsideSupportedRange = (date: string): boolean => {
  const day = moment(date);
  return !day.isValid() || !day.isBetween(FIRST_SUPPORTED_DATE, LAST_SUPPORTED_DATE, "day", "[]");
};

export const handSetDateProblems = (option: OptionDto): HandSetDateProblems => {
  const { startDate, endDate, deadline } = option;
  const start = startDate ? moment(startDate).startOf("day") : undefined;
  return {
    endsBeforeStart: !!start && !!endDate && moment(endDate).startOf("day").isBefore(start),
    deadlineAfterStart: !!start && !!deadline && moment(deadline).startOf("day").isAfter(start),
    outsideSupportedRange: (["startDate", "endDate", "deadline"] as const).filter((field) => {
      const date = option[field];
      return !!date && isOutsideSupportedRange(date);
    }),
  };
};

export const hasHandSetDateProblem = (option: OptionDto): boolean => {
  const problems = handSetDateProblems(option);
  return problems.endsBeforeStart || problems.deadlineAfterStart || problems.outsideSupportedRange.length > 0;
};

export const endsAfterLastSupportedDate = ({ endDate }: OptionDto): boolean =>
  // The BFF (Java) writes a year past 9999 as "+10000-…", which moment reads as an invalid date.
  !!endDate && (endDate.startsWith("+") || moment(endDate).isAfter(LAST_SUPPORTED_DATE, "day"));

export const endsNoLaterThanContract = ({ endDate }: OptionDto, contractEndDate?: string): boolean =>
  !!endDate && !!contractEndDate && !moment(endDate).startOf("day").isAfter(moment(contractEndDate).startOf("day"));
